/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Link, Skeleton, Typography2 } from '@coursera/cds-core';

import {
  EmbeddedSourceCourseNameOptions,
  type EmbeddedSourceCourseNameOptionsType,
} from 'bundles/common/lms-authoring/types/EmbeddedSourceCourseNameOptions';
import type { SourceCoursePartners } from 'bundles/common/lms-authoring/types/SourceCoursePartners';

import _t from 'i18n!nls/authoring';

const styles = {
  name: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  courseNameLink: css`
    color: var(--cds-color-grey-600) !important;
  `,
};

type Props = {
  partners: SourceCoursePartners;
  boldPartnerName: boolean;
  includeCourseName: boolean;
  shortFormContent: boolean;
  courseName?: string;
  courseSlug?: string | null;
  originalName?: string;
};

export const EmbeddedPartnerNameSkeleton: React.FC<{
  includeCourseName?: EmbeddedSourceCourseNameOptionsType;
}> = ({ includeCourseName }) => (
  <Skeleton
    variant="text"
    width={includeCourseName === EmbeddedSourceCourseNameOptions.INLINE ? 300 : 100}
    css={styles.name}
  />
);

const EmbeddedPartnerName: React.FC<Props> = ({
  partners,
  boldPartnerName,
  includeCourseName,
  shortFormContent,
  courseName,
  courseSlug,
  originalName,
}) => {
  const multiplePartners = partners.length > 1;
  const partnerName = multiplePartners ? _t('Multiple educators') : partners[0].name;

  const partnerNameText = boldPartnerName ? (
    <strong>{partnerName}</strong>
  ) : (
    <React.Fragment>{partnerName}</React.Fragment>
  );
  if (!includeCourseName) {
    return partnerNameText;
  }

  if (shortFormContent) {
    return <FormattedMessage message={_t('By {partnerName}')} partnerName={partnerName} />;
  }

  const courseNameLink = courseSlug ? (
    <Link href={`/learn/${courseSlug}`} target="_blank" css={styles.courseNameLink}>
      {courseName}
    </Link>
  ) : (
    courseName
  );

  return (
    <React.Fragment>
      <FormattedMessage
        message={
          multiplePartners
            ? _t('From {courseNameLink} by multiple educators')
            : _t('From {courseNameLink} by {partnerName}')
        }
        courseNameLink={courseNameLink}
        partnerName={partnerName}
      />
      {originalName && _t(", originally titled '#{originalName}'", { originalName })}
    </React.Fragment>
  );
};

type InputProps = Props & {
  className?: string;
};

const EmbeddedPartnerNameWithTypography: React.FC<InputProps> = ({ className, ...props }) => (
  <Typography2
    variant="bodySecondary"
    component="span"
    color="supportText"
    data-testid="embedded-text-content"
    css={styles.name}
    className={className}
  >
    <EmbeddedPartnerName {...props} />
  </Typography2>
);

export default EmbeddedPartnerNameWithTypography;
